import { getColor } from '@/helpers/utils';

/**
 * Chart colors aligned with SERP Overview.
 * Positive/accent slices use the user-selected theme teal
 * (`navbar-vertical-default-link-active-color`), not Bootstrap success green.
 */

/** User-selected accent (سبزآبی تم) — same token SERP charts use. */
export function chartThemeAccent(): string {
  return getColor('navbar-vertical-default-link-active-color');
}

/** Same cycling order as SERP Overview `colorMaker`. */
export function chartPaletteColor(index: number): string {
  if (index % 5 === 0) return chartThemeAccent();
  if (index % 4 === 0) return getColor('info');
  if (index % 3 === 0) return getColor('primary');
  if (index % 2 === 0) return getColor('warning');
  return getColor('danger');
}

export type ChartStatusKey =
  | 'indexed'
  | 'excluded'
  | 'error'
  | 'unknown'
  | 'never'
  | 'success'
  | 'failed'
  | 'partial'
  | 'cancelled'
  | 'other';

export function chartStatusColor(key: ChartStatusKey): string {
  switch (key) {
    case 'indexed':
    case 'success':
      return chartThemeAccent();
    case 'excluded':
    case 'partial':
      return getColor('warning');
    case 'error':
    case 'failed':
      return getColor('danger');
    case 'unknown':
    case 'cancelled':
    case 'other':
      return getColor('gray-500');
    case 'never':
      return getColor('gray-300');
  }
}

export function chartHttpColor(
  key: 'http2xx' | 'http3xx' | 'http4xx' | 'http5xx' | 'unknown',
): string {
  switch (key) {
    case 'http2xx':
      return chartThemeAccent();
    case 'http3xx':
      return getColor('info');
    case 'http4xx':
      return getColor('warning');
    case 'http5xx':
      return getColor('danger');
    case 'unknown':
      return getColor('gray-300');
  }
}

export function chartCanonicalColor(key: string, index = 0): string {
  switch (key) {
    case 'submitted_indexed':
      return chartThemeAccent();
    case 'alternate_canonical':
      return getColor('primary');
    case 'duplicate_google_canonical':
      return getColor('warning');
    case 'duplicate_no_user_canonical':
      return getColor('info');
    case 'other':
      return getColor('gray-500');
    default:
      return chartPaletteColor(index);
  }
}

/** Axis / tooltip / borders — mirrors SERP ECharts chrome. */
export function chartChromeColors() {
  return {
    label: getColor('gray-800'),
    split: getColor('gray-200'),
    line: getColor('gray-300'),
    tooltipBg: getColor('gray-100'),
    tooltipBorder: getColor('gray-300'),
    tooltipText: getColor('gray-1100'),
    legend: getColor('gray-800'),
    sliceBorder: getColor('gray-100'),
  };
}

export function indexOutcomeBarSeries() {
  return [
    { key: 'success', label: 'موفق', color: chartStatusColor('success') },
    { key: 'failed', label: 'ناموفق', color: chartStatusColor('failed') },
  ];
}

export function inspectDailyBarSeries() {
  return [
    { key: 'indexed', label: 'ایندکس‌شده', color: chartStatusColor('indexed') },
    {
      key: 'notIndexed',
      label: 'ایندکس‌نیست',
      color: chartStatusColor('excluded'),
    },
    { key: 'error', label: 'خطا', color: chartStatusColor('error') },
  ];
}

export function inspectBucketBarSeries() {
  return [
    { key: 'indexed', label: 'ایندکس‌شده', color: chartStatusColor('indexed') },
    {
      key: 'excluded',
      label: 'ایندکس‌نیست',
      color: chartStatusColor('excluded'),
    },
    { key: 'error', label: 'خطا', color: chartStatusColor('error') },
  ];
}

export function segmentIndexBarSeries() {
  return [
    ...inspectBucketBarSeries(),
    { key: 'unknown', label: 'نامشخص', color: chartStatusColor('never') },
  ];
}

export function segmentHttpBarSeries() {
  return [
    { key: 'http2xx', label: '۲xx', color: chartHttpColor('http2xx') },
    { key: 'http3xx', label: '۳xx', color: chartHttpColor('http3xx') },
    { key: 'http4xx', label: '۴xx', color: chartHttpColor('http4xx') },
    { key: 'http5xx', label: '۵xx', color: chartHttpColor('http5xx') },
    { key: 'unknown', label: 'نامشخص', color: chartHttpColor('unknown') },
  ];
}
